// Ild-koordineringstiltak (Fire Support Coordination Measures, FSCM). Den
// geospatiale sikkerhetsrammen rundt ild — deconfliction så man ikke skyter i
// egne/forbudte områder. Modellerer NFA/FFA/RFA/ACA (polygon) + FSCL/CFL (linje),
// og sjekker et ildmål mot dem (tillat / nektet / krever koordinering). Ren,
// testbar geometri; kompletterer fireapproval.ts (menneskesignert godkjenning).
import { z } from 'zod';
export const FscmType = z.enum([
    'NFA', // No-Fire Area — ild NEKTET uten særskilt klarering
    'RFA', // Restrictive Fire Area — ild KREVER koordinering
    'FFA', // Free-Fire Area — ild tillatt uten videre klarering
    'ACA', // Airspace Coordination Area — luft/ild-deconfliction
    'FSCL', // Fire Support Coordination Line — utover krever klarering
    'CFL', // Coordinated Fire Line — utover kan skytes fritt
]);
export const FSCM_LABEL = {
    NFA: 'No-Fire Area', RFA: 'Restrictive Fire Area', FFA: 'Free-Fire Area',
    ACA: 'Airspace Coordination Area', FSCL: 'Fire Support Coordination Line', CFL: 'Coordinated Fire Line',
};
export const FSCM_COLOR = {
    NFA: '#ff3b3b', RFA: '#ff8a3d', FFA: '#4bd15b', ACA: '#7a7ad5', FSCL: '#e0a24a', CFL: '#5b9bd5',
};
const isLine = (t) => t === 'FSCL' || t === 'CFL';
export const FscmSchema = z.object({
    id: z.string().min(1).max(64),
    type: FscmType,
    name: z.string().max(120),
    /** Polygon-ring (område) eller polylinje (FSCL/CFL) som [lat,lon]-par. */
    points: z.array(z.tuple([z.number(), z.number()])).min(2),
    effectiveMs: z.number().optional(),
    expiresMs: z.number().optional(),
    issuedBy: z.string().max(120).optional(),
});
export function parseFscm(raw) {
    const r = FscmSchema.safeParse(raw);
    if (!r.success)
        return null;
    if (isLine(r.data.type) === false && r.data.points.length < 3)
        return null; // område krever ≥3 punkt
    return r.data;
}
/** Er tiltaket aktivt på tidspunktet `nowMs`? */
export function fscmActive(m, nowMs) {
    if (m.effectiveMs !== undefined && nowMs < m.effectiveMs)
        return false;
    if (m.expiresMs !== undefined && nowMs > m.expiresMs)
        return false;
    return true;
}
/** Punkt-i-polygon (ray casting) på [lat,lon]-ring. */
export function pointInPolygon(pt, ring) {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const [latI, lonI] = ring[i];
        const [latJ, lonJ] = ring[j];
        const intersect = (lonI > pt.lon) !== (lonJ > pt.lon)
            && pt.lat < ((latJ - latI) * (pt.lon - lonI)) / (lonJ - lonI) + latI;
        if (intersect)
            inside = !inside;
    }
    return inside;
}
/** Hvilken side av en linje (start→slutt) ligger punktet? >0 venstre, <0 høyre. */
export function sideOfLine(pt, a, b) {
    // Kryssprodukt i (lon=x, lat=y)-plan.
    return (b[1] - a[1]) * (pt.lat - a[0]) - (b[0] - a[0]) * (pt.lon - a[1]);
}
/**
 * Sjekk et ildmål mot aktive koordineringstiltak.
 * - NFA som inneholder målet → NEKTET.
 * - RFA som inneholder målet → KREVER KOORDINERING.
 * - FFA som inneholder målet → notat «fri ild».
 * - FSCL: hvis `ownRef` er gitt og målet ligger på motsatt side av eget område
 *   → notat «utover FSCL — krever klarering» (koordinering).
 * Verste utfall vinner.
 */
export function checkFireAgainstMeasures(target, measures, nowMs, ownRef) {
    const violations = [];
    const notes = [];
    let verdict = 'tillatt';
    const escalate = (v) => {
        const rank = { tillatt: 0, 'krever-koordinering': 1, nektet: 2 };
        if (rank[v] > rank[verdict])
            verdict = v;
    };
    for (const m of measures) {
        if (!fscmActive(m, nowMs))
            continue;
        if (m.type === 'NFA' && pointInPolygon(target, m.points)) {
            violations.push({ measure: m, reason: `Mål i No-Fire Area «${m.name}»` });
            escalate('nektet');
        }
        else if (m.type === 'RFA' && pointInPolygon(target, m.points)) {
            violations.push({ measure: m, reason: `Mål i Restrictive Fire Area «${m.name}» — koordinering påkrevd` });
            escalate('krever-koordinering');
        }
        else if (m.type === 'ACA' && pointInPolygon(target, m.points)) {
            violations.push({ measure: m, reason: `Mål i Airspace Coordination Area «${m.name}» — luft/ild-deconfliction` });
            escalate('krever-koordinering');
        }
        else if (m.type === 'FFA' && pointInPolygon(target, m.points)) {
            notes.push(`Fri ild i «${m.name}» (FFA)`);
        }
        else if (m.type === 'FSCL' && ownRef && m.points.length >= 2) {
            // Utover FSCL (motsatt side av eget område) → krever klarering fra høyere.
            const targetSide = sideOfLine(target, m.points[0], m.points[1]);
            const ownSide = sideOfLine(ownRef, m.points[0], m.points[1]);
            if (targetSide !== 0 && ownSide !== 0 && Math.sign(targetSide) !== Math.sign(ownSide)) {
                violations.push({ measure: m, reason: `Mål er UTOVER ${m.name} (FSCL) — krever klarering fra høyere` });
                escalate('krever-koordinering');
            }
        }
    }
    return { verdict, violations, notes };
}
//# sourceMappingURL=firecoord.js.map